import { useRef, useCallback } from 'react';

interface Order {
  id: number;
  siparisId: string;
  masaNumarasi: number;
  kullaniciAdi: string;
  siparisDurumu: string;
  siparisOlusturmaTarihi: string;
  siparisGuncellenmeTarihi?: string;
  toplamTutar: number;
  siparisDetaylari: any[];
  qrMenuId?: string;
  siparisNotu?: string;
}

interface AutoPrintServiceProps {
  onPrintOrder: (order: Order) => Promise<void>;
}

// Global window object'te yazdırılan siparişleri takip et (hot-reload'a karşı)
declare global {
  interface Window {
    __autoPrintedOrders?: Set<number>;
  }
}

export const useAutoPrintService = ({ onPrintOrder }: AutoPrintServiceProps) => {
  // localStorage'dan yazdırılan siparişleri yükle
  const loadPrintedOrders = useCallback(() => {
    try {
      // Önce global window object'ten kontrol et
      if (window.__autoPrintedOrders) {
        return new Set(window.__autoPrintedOrders);
      }
      
      // Bugünün tarihini key'e ekle (gece yarısında otomatik sıfırlanır)
      const today = new Date().toDateString();
      const storageKey = `autoPrintedOrders_${today}`;
      const stored = localStorage.getItem(storageKey);
      
      if (stored) {
        const orderIds = JSON.parse(stored) as number[];
        const orderSet = new Set(orderIds);
        
        // Global window object'e de kaydet
        window.__autoPrintedOrders = orderSet;
        
        return orderSet;
      }
    } catch (error) {
      // Sessizce devam et
    }
    
    const emptySet = new Set<number>();
    window.__autoPrintedOrders = emptySet;
    return emptySet;
  }, []);

  // localStorage'a yazdırılan siparişleri kaydet
  const savePrintedOrders = useCallback((orderIds: Set<number>) => {
    try {
      const today = new Date().toDateString();
      const storageKey = `autoPrintedOrders_${today}`;
      const orderIdsArray = Array.from(orderIds);
      localStorage.setItem(storageKey, JSON.stringify(orderIdsArray));
      
      // Global window object'e de kaydet
      window.__autoPrintedOrders = orderIds;
    } catch (error) {
      // Sessizce devam et
    }
  }, []);

  const printedOrdersRef = useRef<Set<number>>(loadPrintedOrders());
  const printQueueRef = useRef<Order[]>([]);
  const isProcessingRef = useRef<boolean>(false);

  const processQueue = useCallback(async () => {
    if (isProcessingRef.current || printQueueRef.current.length === 0) {
      return;
    }

    isProcessingRef.current = true;

    while (printQueueRef.current.length > 0) {
      const order = printQueueRef.current.shift();
      if (!order) continue;

      // Zaten yazdırıldı mı kontrol et
      if (printedOrdersRef.current.has(order.id)) {
        continue;
      }

      try {
        // Siparişi yazdırıldı olarak işaretle
        printedOrdersRef.current.add(order.id);
        savePrintedOrders(printedOrdersRef.current);
        
        // Yazdırma işlemini gerçekleştir
        await onPrintOrder(order);
        
        // Bir sonraki yazdırma için 3 saniye bekle
        await new Promise(resolve => setTimeout(resolve, 3000));
        
      } catch (error) {
        // Hata durumunda siparişi yazdırıldı listesinden çıkar (tekrar deneme için)
        printedOrdersRef.current.delete(order.id);
        savePrintedOrders(printedOrdersRef.current);
      }
    }

    isProcessingRef.current = false;
  }, [onPrintOrder]);

  const addToQueue = useCallback((order: Order) => {
    // 1. Kontrol: Zaten yazdırıldı mı?
    if (printedOrdersRef.current.has(order.id)) {
      return;
    }

    // 2. Kontrol: Kuyrukta zaten var mı?
    const isAlreadyInQueue = printQueueRef.current.some(item => item.id === order.id);
    if (isAlreadyInQueue) {
      return;
    }

    printQueueRef.current.push(order);
    processQueue();
  }, [processQueue]);

  return { addToQueue };
};
